/* eslint-disable jsx-a11y/aria-role */
// =====================
// Core & React
// =====================
import React, { useEffect, useState, useContext, useMemo } from "react";
import moment from "moment";

// =====================
// Routing
// =====================
import { Link, useLocation, useNavigate } from "react-router-dom";

// =====================
// Third-party utilities
// =====================
import Swal from "sweetalert2";
import { mutate } from "swr";
import { Icon } from "@iconify/react";

// =====================
// MUI – core & styling
// =====================
import {
  Box,
  Typography,
  Drawer as MuiDrawer,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Avatar as MuiAvatar,
} from "@mui/material";

import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import { styled, useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// =====================
// Context & Auth
// =====================
import { AuthContext } from "../context/AuthContextProvider";
import { useColorMode } from "../context/ColorModeProvider";
import { RequireAuth } from "../RequireAuth";

// =====================
// API hooks
// =====================
import { endpoints } from "../utils/axios";
import {
  useNotifications,
  closeNotification,
  acceptNotification,
  rejectNotification,
} from "../api/notification";
import { useGetUserProfile } from "../api/settings";

// =====================
// Components
// =====================
import Loader from "./Loader";
import { BlankPreview } from "../components/BlankPreview";
import CreditMeter from "./Credits/CreditMeter";
import TopUpCreditsModal from "./Credits/TopUpCreditsModal";
import useCreditWallet from "../hooks/useCreditWallet";
import useMobileLayout from "../hooks/useMobileLayout";

// =====================
// Assets
// =====================
import logo_black_full from "../assets/images/logos/spandan-logo.svg";
import logo_short from "../assets/images/logos/spandan-logo-2.svg";
import nopreview from "../assets/images/others/no-preview.png";
import noOrgLogo from "../assets/icons/noOrgLogo.svg";

// =====================
// Config
// =====================
import { menuConfig } from "../config/menuConfig";

// =====================
// Analytics
// =====================
import { trackMenuTabClicked } from "../analytics";

// =====================
// Meta
// =====================
import packageJson from "../../package.json";

const drawerWidth = 240;

// A menu icon is either an Iconify name ("mdi:plus") or an imported image asset
// (an SVG/PNG url), which Iconify can't render — those go through <img> instead.
const ICONIFY_NAME = /^[a-z0-9][a-z0-9-]*:[a-z0-9-]+$/i;
const isIconifyName = (icon) =>
  typeof icon === "string" && ICONIFY_NAME.test(icon);

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  overflowY: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  overflowY: "hidden",
  width: `calc(${theme.spacing(9)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(10)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1.5),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  "& .MuiDrawer-paper": {
    overflowY: "auto",

    /* Hide scrollbar (works everywhere) */
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE/Edge
  },

  "& .MuiDrawer-paper::-webkit-scrollbar": {
    display: "none", // Chrome, Safari
  },

  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

// eslint-disable-next-line react/prop-types
const MiniMaxHeader = ({ sidebar = true }) => {
  const theme = useTheme();
  const isMobile = useMobileLayout();

  const {
    logout,
    user,
    open,
    handleDrawerToggle,
    handleMobileDrawerToggle,
    closeMobileDrawer,
    mobileOpen,
    onReceivePushNotification,
  } = useContext(AuthContext);

  // The mobile overlay always shows the full-width menu; only the desktop rail
  // collapses to the mini variant.
  const expanded = isMobile || open;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);

  const {
    credits: walletCredits,
    isUnlimitedCredits: walletIsUnlimitedCredits,
    currentPlan: walletCurrentPlan,
    autoSendEnabled: walletAutoSendEnabled,
    pricePerCredit: walletPricePerCredit,
    canTopUp,
    topUpOpen: topUpModalOpen,
    openTopUp,
    closeTopUp,
    paying: topUpPaying,
    handlePayTopUp,
  } = useCreditWallet();

  const profileMenuOpen = Boolean(profileAnchor);
  // Migrated businesses only see a reduced nav (menuConfig items tagged
  // migrationAllowed) - userType stays plain "b2b", migration is a separate flag.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;
  const { mode, toggle: toggleColorMode } = useColorMode();
  const profileItemSx = {
    py: 1.1,
    px: 2,
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Figtree",
    "&:hover": { backgroundColor: "var(--app-hover)" },
  };
  // Realtime toast state for incoming FCM messages (separate from existing notifications)
  const [refreshNoti, setRefreshNoti] = useState(false);
  const STORAGE_KEY = "handled_case_reports";
  const isWithinFiveHours = (createdAt) => {
    if (!createdAt) return false;

    const createdTime = new Date(createdAt).getTime();
    const now = Date.now();

    const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;

    return now - createdTime <= FIVE_HOURS_MS;
  };

  const breakAfterTwoWords = (text) => {
    if (!text) return "";

    const words = text.split(" ");
    let lines = [];

    for (let i = 0; i < words.length; i += 2) {
      lines.push(words.slice(i, i + 2).join(" "));
    }

    return lines.join("\n");
  };

  const getHandledReports = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
    } catch {
      return new Set();
    }
  };

  const saveHandledReports = (set) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  };

  const [handledReports, setHandledReports] = useState(() =>
    getHandledReports(),
  );

  const { userProfile: userData } = useGetUserProfile(
    {},
    { revalidateOnFocus: false, dedupingInterval: 60000 },
  );

  const { notifications = [], isLoading: notificationLoader } =
    useNotifications(
      sidebarOpen ? { reset: refreshNoti } : { limit: 1, reset: refreshNoti },
    );
  const navigate = useNavigate();

  const [actionLoading, setActionLoading] = useState({});
  const NOTIFICATION_DURATION = 5000;

  const setLoadingFor = (id, val) => {
    setActionLoading((prev) => ({ ...prev, [id]: val }));
  };

  const shownToastIdsRef = React.useRef(new Set());

  const handleAcceptNotification = async (item, e) => {
    e?.stopPropagation();

    const reportId = item.activity_id;

    if (
      !item ||
      item.is_read ||
      actionLoading[reportId] ||
      handledReports.has(reportId)
    ) {
      return;
    }

    setLoadingFor(reportId, true);

    try {
      await acceptNotification({ reportId, status: "accepted" });

      const updatedSet = new Set(handledReports);
      updatedSet.add(reportId);
      setHandledReports(updatedSet);
      saveHandledReports(updatedSet);

      setVisibleNotifications((prev) =>
        prev.map((n) =>
          n._id === item._id
            ? {
              ...n,
              is_read: true,
              is_action_required: false,
            }
            : n,
        ),
      );

      mutate(
        [endpoints.notifications.getNotifications, {}],
        (prev = []) =>
          prev.map((n) =>
            n._id === item._id
              ? { ...n, is_read: true, is_action_required: false }
              : n,
          ),
        false,
      );

      Swal.fire({
        icon: "success",
        title: "Accepted",
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to accept" });
    } finally {
      setLoadingFor(reportId, false);
    }
  };

  const handleRejectNotification = async (item, e) => {
    e?.stopPropagation();

    const reportId = item.activity_id;

    if (
      !item ||
      item.is_read ||
      actionLoading[reportId] ||
      handledReports.has(reportId)
    ) {
      return;
    }

    setLoadingFor(reportId, true);

    try {
      await rejectNotification({ reportId, status: "rejected" });

      const updatedSet = new Set(handledReports);
      updatedSet.add(reportId);
      setHandledReports(updatedSet);
      saveHandledReports(updatedSet);

      setVisibleNotifications((prev) =>
        prev.map((n) =>
          n._id === item._id
            ? {
              ...n,
              is_read: true,
              is_action_required: false,
            }
            : n,
        ),
      );

      mutate([endpoints.notifications.getNotifications, {}]);

      Swal.fire({
        icon: "success",
        title: "Rejected",
        toast: true,
        position: "top-end",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Failed to reject" });
    } finally {
      setLoadingFor(reportId, false);
    }
  };

  const isActionDisabled = (item) =>
    actionLoading[item.activity_id] ||
    item.is_read ||
    handledReports.has(item.activity_id);

  useEffect(() => {
    // if (
    //   onReceivePushNotification?.data?.interpretation_report_status ===
    //   "critical"
    // )
    //  {
    setRefreshNoti((prev) => !prev);
    // }
  }, [onReceivePushNotification]);

  const width = 20;

  const handleMarkRead = async (item) => {
    setVisible(false);
    if (!item || item.is_read) return;
    try {
      await closeNotification({ notificationIds: [item._id] });
    } catch (e) {
      console.error("Mark as read failed:", e);
    }
  };

  useEffect(() => {
    if (!notifications?.length) return;

    const latest = notifications[0];

    if (
      latest.is_read ||
      handledReports.has(latest.activity_id) ||
      !isWithinFiveHours(latest.when)
    ) {
      return;
    }

    if (shownToastIdsRef.current.has(latest._id)) return;

    shownToastIdsRef.current.add(latest._id);

    setVisibleNotifications((prev) => [
      {
        ...latest,
        startedAt: Date.now(),
        isClosing: false,
      },
      ...prev,
    ]);
  }, [notifications, handledReports]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleNotifications((prev) =>
        prev.map((n) => {
          const elapsed = Date.now() - n.startedAt;

          // start slide-out at 5 sec
          if (elapsed >= NOTIFICATION_DURATION && !n.isClosing) {
            return { ...n, isClosing: true };
          }

          return n;
        }),
      );

      // remove after animation (300ms)
      setTimeout(() => {
        setVisibleNotifications((prev) => prev.filter((n) => !n.isClosing));
      }, 300);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (sidebarOpen) mutate([endpoints.notifications.getNotifications, {}]);
  }, [sidebarOpen]);

  useEffect(() => {
    const body = document.body;
    // On mobile the drawer floats above the page, so the content must not be
    // offset by the rail width — leave both classes off.
    body.classList.toggle("layout-drawer-open", !isMobile && open);
    body.classList.toggle("layout-drawer-closed", !isMobile && !open);
    return () => {
      body.classList.remove("layout-drawer-open");
      body.classList.remove("layout-drawer-closed");
    };
  }, [open, isMobile]);

  // Navigating from the mobile menu should dismiss the overlay.
  useEffect(() => {
    if (isMobile) closeMobileDrawer?.();
  }, [location.pathname, isMobile, closeMobileDrawer]);

  const getProgress = (startedAt) => {
    const elapsed = Date.now() - startedAt;
    const percent = (elapsed / NOTIFICATION_DURATION) * 100;
    return Math.min(100, Math.max(0, percent));
  };

  const TOGGLER_OPEN_PX = 230; // full drawer edge
  const TOGGLER_CLOSED_PX = 70;

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ---------- FIXED HEADER (logo + business) ---------- */}
      <Box sx={{ flexShrink: 0 }}>
        <DrawerHeader
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.5,
          }}
        >
          {/* Logo (changes with open state) */}
          <Link
            to="/dashboard"
            onClick={() => isMobile && closeMobileDrawer?.()}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            <img
              src={expanded ? logo_black_full : logo_short}
              alt="Logo"
              style={{
                width: expanded ? "180px" : "120px",
                height: expanded ? "40px" : "44px",
                maxWidth: "100%",
                maxHeight: "48px",
                objectFit: "contain",
                transition: "all 0.3s ease",
                filter: mode === "dark" ? "brightness(0) invert(1)" : "none",
              }}
            />
          </Link>

          {/* Close button — mobile only, the overlay has no other affordance */}
          {isMobile && (
            <IconButton
              onClick={closeMobileDrawer}
              aria-label="Close menu"
              edge="end"
              sx={{
                flexShrink: 0,
                ml: 0.5,
                width: 40, // comfortable touch target
                height: 40,
                color: "var(--app-text)",
              }}
            >
              <ChevronLeftIcon
                sx={{ color: "var(--app-text-muted)", fontSize: 22 }}
              />
            </IconButton>
          )}
        </DrawerHeader>

        <Divider style={{ marginTop: "0px" }} />
        <Box
          sx={{
            position: "relative",
            overflow: "visible",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: expanded ? "space-between" : "center",
              width: "100%",
              px: expanded ? 2 : 0,
              transition: "all 0.3s ease",
              mt: 1,
              mb: 2,
              position: "relative",
            }}
          >
            {/* --- Left: Center Logo Section --- */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: expanded ? "auto" : "100%",
                transition: "all 0.3s ease",
                position: "relative",
              }}
            >
              {/* Square Logo */}
              <Box
                sx={{
                  width: 45,
                  height: 45,
                  // backgroundColor: "var(--app-text)",
                  borderRadius: "5px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {user?.businessLogo ? (
                  <img
                    src={user?.businessLogo}
                    alt="logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <img
                    src={noOrgLogo}
                    alt="logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Box>

              {/* Business Name — only visible when open */}
              {expanded && (
                <Box sx={{ ml: 1, maxWidth: "160px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--app-text)",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                      lineHeight: "1.2em",
                    }}
                  >
                    {breakAfterTwoWords(user?.businessName)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ---------- SCROLLABLE NAV ---------- */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#E2E3E7",
            borderRadius: "8px",
          },
        }}
      >
        <List sx={{ py: 1 }}>
          {menuConfig.map((section, sectionIndex) => {
            // Hide the whole section (incl. its header) when the current user
            // has no items in it for their role. Migrated businesses only get
            // items explicitly tagged migrationAllowed.
            const hasVisibleItem = section.data.some((item) =>
              item.role.includes(user?.userType) && (!isMigratedUser || item.migrationAllowed),
            );
            if (!hasVisibleItem) return null;
            return (
              <Box key={sectionIndex}>
                {/* Section Header */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    pl: expanded ? 3 : 0, //  remove left padding when closed
                    pt: sectionIndex === 0 ? 1 : 1,
                    fontSize: expanded ? "16px" : "14px",
                    fontWeight: 600,
                    color: "var(--app-text)",
                    textAlign: expanded ? "left" : "center", //  center align when closed
                    width: "100%", // ensures proper centering
                    fontFamily: "Figtree",
                  }}
                >
                  {expanded ? section.label : section.label2}
                </Typography>

                {/* Section Items */}
                {section.data.map((item, itemIndex) => {
                  const isActive = location.pathname.startsWith(item.path);

                  if (!item.role.includes(user?.userType) || (isMigratedUser && !item.migrationAllowed)) return null;

                  return (
                    <RequireAuth
                      key={`${sectionIndex}-${itemIndex}-wrap`}
                      role={[
                        "b2b",
                        "b2b-admin",
                        "b2b-member",
                        "b2b-doctor",
                        "b2b-viewer",
                      ]}
                      permission={item.permission}
                    >
                      <ListItem
                        // disablePadding
                        sx={{
                          display: "block",
                          paddingLeft: expanded ? "7px" : "5px",
                          paddingRight: expanded ? "7px" : "5px",
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                      >
                        <ListItemButton
                          component={Link}
                          to={item.path}
                          onClick={() => {
                            trackMenuTabClicked(item.title, item.path);
                            if (isMobile) closeMobileDrawer?.();
                          }}
                          sx={[
                            {
                              minHeight: 48,
                              px: 2.5,
                              borderRadius: "10px",
                              flexDirection: expanded ? "row" : "column",
                              color: isActive ? "#fff" : "var(--app-text)",
                              backgroundColor: isActive
                                ? "#5751E9"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: isActive
                                  ? "#5751E9"
                                  : "var(--app-surface-2)",
                              },
                            },
                            expanded
                              ? { justifyContent: "initial" }
                              : { justifyContent: "center" },
                          ]}
                        >
                          <ListItemIcon
                            sx={[
                              {
                                minWidth: 0,
                                justifyContent: "center",
                                color: isActive ? "#fff" : "var(--app-text)",
                              },
                              expanded ? { mr: 1 } : { mr: "3" },
                            ]}
                          >
                            {!isIconifyName(item.icon) ? (
                              <img
                                src={item.icon}
                                width={width}
                                height={width}
                                style={{
                                  filter: isActive
                                    ? "brightness(0) invert(1)"
                                    : mode === "dark"
                                      ? "brightness(0) invert(0.85)"
                                      : "brightness(0)",
                                }}
                                alt={item.title}
                              />
                            ) : (
                              <Icon
                                icon={item.icon}
                                width={width}
                                height={width}
                                style={{
                                  color: isActive ? "#fff" : "var(--app-text)",
                                }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            sx={[
                              expanded
                                ? {
                                  opacity: 1,
                                  fontWeight: isActive ? 600 : 400,
                                  ml: 1,
                                  textAlign: "left",
                                  "& .MuiListItemText-primary": {
                                    fontSize: "16px",
                                    color: isActive
                                      ? "#fff"
                                      : "var(--app-text)",
                                    transition: "color 0.3s ease",
                                    whiteSpace: "nowrap", // keep single line when sidebar is open
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }
                                : {
                                  opacity: 1,
                                  mt: "4px",
                                  textAlign: "center",
                                  "& .MuiListItemText-primary": {
                                    fontSize: "10px",
                                    lineHeight: "1.2",
                                    color: isActive
                                      ? "#fff"
                                      : "var(--app-text)",
                                    transition: "color 0.3s ease",
                                    whiteSpace: "normal", //  allow wrapping when closed
                                    overflow: "visible",
                                    textOverflow: "clip", // no ellipsis
                                  },
                                  fontWeight: isActive ? 600 : 400,
                                },
                            ]}
                          />
                        </ListItemButton>
                      </ListItem>
                    </RequireAuth>
                  );
                })}
              </Box>
            );
          })}
        </List>
      </Box>

      {/* ---------- FIXED FOOTER (logout + version) ---------- */}
      <Box sx={{ flexShrink: 0, borderTop: "1px solid var(--app-border)" }}>
        <List sx={{ py: 0.5 }}>
          {/* Logout */}
          <ListItem disablePadding sx={{ display: "block", mt: 0 }}>
            <ListItemButton
              onClick={() => setOpenLogoutDialog(true)}
              sx={[
                {
                  minHeight: 48,
                  flexDirection: expanded ? "row" : "column",
                  px: 2.5,
                  color: "#E72020",
                  "&:hover": { backgroundColor: "#ffeaea" },
                },
                expanded
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                    color: "#E72020",
                  },
                  expanded ? { mr: 1, ml: 1 } : { mr: "3" },
                ]}
              >
                <Icon
                  icon={"majesticons:logout"}
                  width={width}
                  height={width}
                />
              </ListItemIcon>

              <ListItemText
                primary="Log Out"
                sx={[
                  expanded
                    ? {
                      opacity: 1,
                      ml: 1,
                      textAlign: "left",
                      "& .MuiListItemText-primary": {
                        fontSize: "16px",
                      },
                    }
                    : {
                      opacity: 1,
                      mt: "4px",
                      textAlign: "center",
                      "& .MuiListItemText-primary": {
                        fontSize: "10px",
                        lineHeight: "1.2",
                      },
                    },
                ]}
              />
            </ListItemButton>
          </ListItem>
        </List>
        {expanded && (
          <Box
            sx={{
              pb: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "10px",
                textAlign: "center",
                opacity: 0.7,
              }}
            >
              Version {packageJson.version}
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog
        open={openLogoutDialog}
        onClose={() => setOpenLogoutDialog(false)}
        aria-labelledby="logout-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            padding: "0px",
            width: "508px",
            boxShadow: "var(--app-shadow)",
            position: "relative",
          },
        }}
      >
        {/* Close Icon */}
        <Icon
          icon="mdi:close"
          width="26"
          height="26"
          onClick={() => setOpenLogoutDialog(false)}
          style={{
            position: "absolute",
            right: 16,
            top: 16,
            cursor: "pointer",
            color: "#444",
          }}
        />

        {/* Title + Icon */}
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            textAlign: "center",
            fontWeight: "700",
            fontSize: "22px",
            mb: 1,
            mt: 2,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            {/* Warning Icon */}
            <Icon
              icon="fa:exclamation-triangle"
              width="65"
              height="65"
              style={{ color: "#E72020", marginBottom: "12px" }}
            />
            Log Out?
          </Box>
        </DialogTitle>

        {/* Body */}
        <DialogContent sx={{ px: 4 }}>
          <Typography
            align="center"
            variant="body1"
            sx={{
              color: "var(--app-text-muted)",
              fontSize: "16px",
              lineHeight: "1.6",
              mt: 1,
            }}
          >
            Are you sure you want to log out?
          </Typography>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            justifyContent: "center",
            mt: 1,
            gap: 1,
            pb: 2,
          }}
        >
          {/* Confirm Log Out Button */}
          <Button
            className="btn btn-added-primary swal-pop-btn"
            variant="contained"
            size="medium"
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 4,
              backgroundColor: "#5A4FFA", // purple like previous dialog
              color: "white",
              fontWeight: "600",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#4A3EEA",
                boxShadow: "none",
              },
            }}
            onClick={() => {
              logout();
              setOpenLogoutDialog(false);
            }}
          >
            Log Out
          </Button>

          {/* Cancel Button */}
          <Button
            className="btn btn-added-primary2 swal-pop-btn"
            variant="contained"
            size="medium"
            sx={{
              textTransform: "none",
              borderRadius: "12px",
              px: 4,
              backgroundColor: "#FDE3E3",
              color: "#C62828",
              fontWeight: "600",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#F8D3D3",
                boxShadow: "none",
              },
            }}
            onClick={() => setOpenLogoutDialog(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <>
      {/* Header */}
      <div className="header  w-100">
        <div className="header-left border-0 w-3 d-flex justify-content-start">
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ ml: 0.5, color: "var(--app-text)" }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </div>
        {/* Desktop-only rail collapse/expand handle */}
        <IconButton
          onClick={handleDrawerToggle}
          aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
          aria-expanded={open}
          size="small"
          sx={{
            display: isMobile ? "none" : "inline-flex",
            position: "fixed", // floats independent of layout
            top: { xs: 72, md: "14vh" }, // tweak as you like
            ...(theme.direction === "rtl"
              ? { right: open ? TOGGLER_OPEN_PX : TOGGLER_CLOSED_PX }
              : { left: open ? TOGGLER_OPEN_PX : TOGGLER_CLOSED_PX }),
            transform: "translateY(-50%)",
            transition: (theme) =>
              theme.transitions.create(
                [
                  "left",
                  "right",
                  "transform",
                  "box-shadow",
                  "background-color",
                ],
                {
                  duration: 300,
                  easing: theme.transitions.easing.easeInOut,
                },
              ),
            border: "1px solid #E3E8EF",
            borderRadius: "8px",
            bgcolor: open ? "var(--app-surface-2)" : "var(--app-surface-2)",
            width: 28,
            height: 28,
            zIndex: theme.zIndex.drawer + 1, // above drawer, below modals
            pointerEvents: "auto",
            boxShadow: open
              ? "0 4px 16px rgba(52, 152, 219, 0.10)"
              : "0 2px 8px rgba(0,0,0,0.09)",
            "&:hover": {
              bgcolor: "#E8F4FD",
              borderColor: "#B6DCF6",
              transform: "translateY(-50%) scale(1.08)",
              boxShadow: "0 6px 20px rgba(52, 152, 219, 0.13)",
            },
            "&:active": {
              bgcolor: "#D6EAF8",
              boxShadow: "0 2px 4px rgba(0,0,0,0.12)",
            },
          }}
        >
          {open ? (
            <ChevronLeftIcon
              sx={{ color: "var(--app-text-muted)", fontSize: 18 }}
            />
          ) : (
            <ChevronRightIcon
              sx={{ color: "var(--app-text-muted)", fontSize: 18 }}
            />
          )}
        </IconButton>

        <ul className="nav user-menu w-9 d-flex justify-content-end">
          <li>
            {!sidebarOpen && visibleNotifications.length > 0 && (
              <Box
                sx={{
                  position: "fixed",
                  top: 80,
                  right: 20,
                  width: "26%",
                }}
              >
                {visibleNotifications.map((n) => (
                  <Box
                    key={n._id}
                    sx={{
                      backgroundColor: n.is_action_required
                        ? "#fff5f5"
                        : "#ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      borderRadius: "10px",
                      p: "14px 18px",
                      fontFamily: "Figtree",
                      position: "relative",
                      overflow: "hidden",
                      transform: n.isClosing
                        ? "translateX(120%)"
                        : "translateX(0)",
                      opacity: n.isClosing ? 0 : 1,
                      transition: "all 0.3s ease-in-out",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "15px",
                        mb: "5px",
                        fontFamily: "Figtree",
                      }}
                      dangerouslySetInnerHTML={{ __html: n.purpose }}
                    />

                    <Typography
                      sx={{
                        fontSize: "12px",
                        mb: "6px",
                        fontSize: "14px",
                        fontFamily: "Figtree",
                      }}
                      dangerouslySetInnerHTML={{ __html: n.text }}
                    />

                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "var(--app-text-muted)",
                        fontSize: "14px",
                        fontFamily: "Figtree",
                      }}
                    >
                      {moment(n.when).fromNow()}
                    </Typography>
                    {n.activity === "CASE_INVITATION" &&
                      n.is_action_required &&
                      !n.is_read &&
                      !handledReports.has(n.activity_id) && (
                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={isActionDisabled(n)}
                            onClick={(e) => handleAcceptNotification(n, e)}
                          >
                            Accept
                          </Button>

                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={isActionDisabled(n)}
                            onClick={(e) => handleRejectNotification(n, e)}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                    <Box sx={{ mt: 0.8 }}>
                      {n.activity === "CASE_REFERRAL" && (
                        <Typography
                          component="span"
                          sx={{
                            color: "#4F46E5",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "14px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/cases");
                          }}
                        >
                          View Case
                        </Typography>
                      )}

                      {n.activity === "INTERPRETATION_REQUEST" && (
                        <Typography
                          component="span"
                          sx={{
                            color: "#4F46E5",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "14px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/ecg-reports/${n.activity_id}`);
                          }}
                        >
                          View Report
                        </Typography>
                      )}

                      {n.activity === "CALL_JOIN" && !n.is_read && (
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            mt: 1,
                            backgroundColor: "#28C840",
                            borderRadius: "20px",
                            textTransform: "none",
                            fontWeight: 600,
                            px: 2,
                            "&:hover": {
                              backgroundColor: "#22B737",
                            },
                          }}
                          startIcon={
                            <Icon
                              icon="majesticons:video"
                              width="18"
                              height="18"
                            />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/onlineconsultation/${n.activity_id}`);
                          }}
                        >
                          Join
                        </Button>
                      )}
                    </Box>

                    {/* PROGRESS BAR */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        height: "3px",
                        width: `${getProgress(n.startedAt)}%`,
                        backgroundColor: "#BCBAFF",
                        transition: "width 1s linear",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </li>
          {user?.userType == "b2b" &&
            !isMigratedUser &&
            walletAutoSendEnabled &&
            !walletIsUnlimitedCredits &&
            walletCredits <= 0 && (
              <li className="nav-item" style={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    height: "40px",
                    padding: "0 10px",
                    borderRadius: "999px",
                    border: "1px solid #F4D88A",
                    backgroundColor: "#FFFBF0",
                    color: "#8B6200",
                    fontSize: "12px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Icon icon="material-symbols:warning-outline-rounded" width="14" height="14" />
                  Auto-send paused — no credits
                </Box>
              </li>
            )}
          {user?.userType == "b2b" && (
            <li
              className="nav-item credit-meter-wrapper"
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 1,
                minWidth: 0,
              }}
            >
              <CreditMeter
                balance={walletCredits}
                isUnlimited={walletIsUnlimitedCredits}
                isMigrated={isMigratedUser || walletCurrentPlan?.isMigrated === true}
                planName={walletCurrentPlan?.planName || walletCurrentPlan?.identifier}
                showTopUp={canTopUp}
                onTopUp={openTopUp}
              />
            </li>
          )}

          <li className="nav-item dropdown">
            <IconButton
              aria-label="Open notifications"
              onClick={() => setSidebarOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSidebarOpen(true);
                }
              }}
              sx={{
                mt: "2px",
                p: 0.5,
                color: theme.palette.mode === "dark" ? "#fff" : "#000",
              }}
            >
              <Icon icon="mdi:bell" width={28} height={28} />
            </IconButton>
          </li>
          <li className="nav-item dropdown">
            <IconButton
              onClick={(e) => setProfileAnchor(e.currentTarget)}
              aria-label="account menu"
              sx={{ ml: 1.5, mr: 1, p: 0.25 }}
            >
              <MuiAvatar
                src={userData?.logo}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#5751E9",
                  fontSize: 15,
                  fontWeight: 700,
                  border: "2px solid #ECEBFD",
                }}
              >
                {(userData?.firstName || "").charAt(0).toUpperCase()}
              </MuiAvatar>
            </IconButton>

            <Menu
              anchorEl={profileAnchor}
              open={profileMenuOpen}
              onClose={() => setProfileAnchor(null)}
              onClick={() => setProfileAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.25,
                  width: 272,
                  borderRadius: "14px",
                  border: "1px solid var(--app-border)",
                  boxShadow: "0 12px 32px rgba(16,24,40,0.14)",
                  overflow: "hidden",
                  p: 0,
                },
              }}
              MenuListProps={{ sx: { py: 0 } }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.75,
                  background:
                    "linear-gradient(180deg, var(--app-surface-2) 0%, var(--app-surface) 100%)",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "var(--app-text)",
                    lineHeight: 1.3,
                    fontFamily: "Figtree",
                  }}
                >
                  {userData?.firstName} {userData?.lastName}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: 12.5,
                    color: "var(--app-text-muted)",
                    fontFamily: "Figtree",
                    mt: 0.25,
                  }}
                >
                  {user?.userType == "b2b"
                    ? "Super Admin"
                    : user?.userType == "b2b-admin"
                      ? "Admin"
                      : user?.userType == "b2b-doctor"
                        ? "Doctor"
                        : user?.userType == "b2b-viewer"
                          ? "Viewer"
                          : "Member"}{" "}
                  · {user?.phoneNo || user?.virtualId}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "var(--app-border)" }} />

              <MenuItem onClick={() => navigate("/profile")} sx={profileItemSx}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Icon
                    icon="solar:user-circle-linear"
                    width={20}
                    color="#5751E9"
                  />
                </ListItemIcon>
                My Profile
              </MenuItem>

              {/* <MenuItem onClick={toggleColorMode} sx={profileItemSx}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Icon
                    icon={
                      mode === "dark"
                        ? "solar:sun-2-linear"
                        : "solar:moon-linear"
                    }
                    width={20}
                    color="#5751E9"
                  />
                </ListItemIcon>
                {mode === "dark" ? "Light mode" : "Dark mode"}
              </MenuItem> */}

              <Divider sx={{ borderColor: "var(--app-border-2)" }} />

              <MenuItem
                onClick={() => setOpenLogoutDialog(true)}
                sx={{
                  ...profileItemSx,
                  color: "#E5484D",
                  "&:hover": { backgroundColor: "#FEECEC" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Icon icon="majesticons:logout" width={20} color="#E5484D" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </li>
        </ul>
      </div>

      {/* Permanent drawer for desktop */}
      {!isMobile && (
        <Drawer variant="permanent" open={open}>
          {/* your drawer content */}
          {drawerContent}
        </Drawer>
      )}

      {/* Temporary drawer for mobile */}
      {isMobile && (
        <MuiDrawer
          id="mobile-nav-drawer"
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={closeMobileDrawer}
          ModalProps={{
            keepMounted: true, // Better performance on mobile
          }}
          transitionDuration={{ enter: 260, exit: 220 }}
          sx={{
            zIndex: theme.zIndex.drawer + 2, // above the fixed app header
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              maxWidth: "85vw", // keep the backdrop tappable on small phones
              boxSizing: "border-box",
              overflowX: "hidden",
              // Transform-only animation keeps the slide smooth on mobile GPUs
              willChange: "transform",
            },
          }}
        >
          {/* same content as your permanent drawer */}
          {drawerContent}
        </MuiDrawer>
      )}

      {/* Right Notifications Drawer (MUI) */}

      <MuiDrawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": { width: 400, maxWidth: "90vw", zIndex: 1 },
        }}
      >
        <Toolbar sx={{ px: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Notifications
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            aria-label="Close notifications"
            edge="end"
          >
            {theme.direction === "rtl" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </Toolbar>
        <Divider />
        {notificationLoader ? (
          <Loader />
        ) : (
          <Box sx={{ p: 0 }}>
            {notifications.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <BlankPreview
                  icon={nopreview}
                  title="No Notifications Available"
                  subtitle=""
                />
              </Box>
            ) : (
              <>
                {notifications.map((item) => {
                  const hasActivity =
                    item.activity &&
                    (item.activity === "CASE_REFERRAL" ||
                      item.activity === "CASE_INVITATION" ||
                      item.activity === "INTERPRETATION_REQUEST" ||
                      item.activity === "CALL_JOIN");

                  const timeText = moment(item.when).fromNow();
                  const isJustNow = timeText.toLowerCase().includes("just");
                  const isCase =
                    item.activity === "CASE_REFERRAL" ||
                    item.activity === "CASE_INVITATION" ||
                    item.activity === "CASE_CREATED";

                  const textLower = item.text.toLowerCase();

                  let leftBorderColor = "var(--app-border)";
                  if (textLower.includes("normal")) leftBorderColor = "#348530";
                  if (textLower.includes("abnormal"))
                    leftBorderColor = "#E4841A";
                  if (textLower.includes("critical"))
                    leftBorderColor = "#D34848";

                  let backgroundColor = "var(--app-surface)";
                  if (isJustNow) backgroundColor = "#F3F6FF";
                  if (isCase) backgroundColor = "#FFECD6";

                  return (
                    <Box
                      key={item._id}
                      onClick={() => handleMarkRead(item)}
                      sx={{
                        display: "flex",
                        gap: 2,
                        py: 2,
                        borderBottom: "1px solid var(--app-border)",
                        backgroundColor,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: isJustNow
                            ? "#EEF2FF"
                            : isCase
                              ? "#FFE3C2"
                              : "var(--app-surface-2)",
                        },
                      }}
                    >
                      {/* LEFT BORDER */}
                      <Box
                        sx={{
                          width: 4,
                          borderRadius: "4px",
                          backgroundColor: leftBorderColor,
                          flexShrink: 0,
                        }}
                      />

                      {/* CONTENT */}
                      <Box sx={{ flex: 1 }}>
                        {/* TITLE */}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "16px",
                            mb: 0.5,
                            color: "var(--app-text)",
                          }}
                          dangerouslySetInnerHTML={{ __html: item.purpose }}
                        />

                        {/* MESSAGE */}
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "var(--app-text)",
                            lineHeight: 1.5,
                          }}
                          dangerouslySetInnerHTML={{ __html: item.text }}
                        />

                        {/* ACTIONS */}
                        {hasActivity && (
                          <>
                            {item.activity === "CASE_INVITATION" &&
                              item.is_action_required &&
                              !item.is_read &&
                              !handledReports.has(item.activity_id) &&
                              isWithinFiveHours(item.created_at) && (
                                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disabled={isActionDisabled(item)}
                                    onClick={(e) =>
                                      handleAcceptNotification(item, e)
                                    }
                                  >
                                    Accept
                                  </Button>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={isActionDisabled(item)}
                                    onClick={(e) =>
                                      handleRejectNotification(item, e)
                                    }
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              )}

                            {/* NAVIGATION / JOIN */}
                            <Box sx={{ mt: 0.8 }}>
                              {item.activity === "CASE_REFERRAL" && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "#4F46E5",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    fontSize: "14px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/cases");
                                  }}
                                >
                                  View Case
                                </Typography>
                              )}

                              {item.activity === "INTERPRETATION_REQUEST" && (
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "#4F46E5",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    fontSize: "14px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/ecg-reports/${item.activity_id}`,
                                    );
                                  }}
                                >
                                  View Report
                                </Typography>
                              )}

                              {item.activity === "CALL_JOIN" &&
                                !item.is_read && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={{
                                      mt: 1,
                                      backgroundColor: "#28C840",
                                      borderRadius: "20px",
                                      textTransform: "none",
                                      fontWeight: 600,
                                      px: 2,
                                      "&:hover": {
                                        backgroundColor: "#22B737",
                                      },
                                    }}
                                    startIcon={
                                      <Icon
                                        icon="majesticons:video"
                                        width="18"
                                        height="18"
                                      />
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/onlineconsultation/${item.activity_id}`,
                                      );
                                    }}
                                  >
                                    Join
                                  </Button>
                                )}
                            </Box>
                          </>
                        )}

                        {/* TIME */}
                        <Typography
                          sx={{
                            mt: 1,
                            fontSize: "13px",
                            color: isJustNow
                              ? "#4F46E5"
                              : "var(--app-text-muted)",
                            fontWeight: isJustNow ? 600 : 400,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {isJustNow ? "Just Now" : timeText}
                          {isJustNow && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "#4F46E5",
                              }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        )}
      </MuiDrawer>
      {/* Top Up Credits Modal */}
      <TopUpCreditsModal
        open={topUpModalOpen}
        onClose={closeTopUp}
        currentBalance={walletCredits}
        pricePerCredit={walletPricePerCredit}
        loading={topUpPaying}
        onPayTopUp={handlePayTopUp}
      />
    </>
  );
};

export default MiniMaxHeader;
